import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {HeaderService} from '../../../core/services/header.service';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';
import {AuthService} from '../../../core/security/auth.service';
import {merge} from 'rxjs';
import {RolesEnum} from '../../../core/security/roles.enum';
import {MatPaginator} from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {debounceTime, tap} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {AuditService as AuditApiService} from '../../../api/services/audit.service';
import {AuditDatasource} from './audit.datasource';
import {AuditListRequest} from '../../../api/models/audit-list-request';
import {LookupModel} from '../../../api/models/lookup-model';
import {AuditService} from '../../audit.service';
import {AuditModel} from '../../../api/models/audit-model';
import {AuditTypeEnum} from '../audit-type.enum';
import {RouterHelper} from '../../../core/utilities/router-helper';
import {AuditCategoryEnum} from '../audit-category.enum';
import { FlexModule } from '@angular/flex-layout';
import { NgIf, AsyncPipe } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { DateTimePipe } from '../../../core/pipes/date-time.pipe';

@Component({
    selector: 'app-audit-grid',
    templateUrl: './audit-grid.component.html',
    styleUrls: ['./audit-grid.component.scss'],
    standalone: true,
    imports: [
        FlexModule,
        NgIf,
        MatProgressSpinner,
        MatTable,
        MatSort,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatSortHeader,
        MatCellDef,
        MatCell,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatPaginator,
        AsyncPipe,
        DateTimePipe,
    ],
})
export class AuditGridComponent implements OnDestroy, OnInit, AfterViewInit {
  displayedColumns: string[] = [
    'date',
    'type',
    'category',
    'username',
    'details',
  ];
  @Input() filterChangeEvent: EventEmitter<any>;
  @Input() auditTypes: LookupModel[] = [];
  @Input() auditCategories: LookupModel[] = [];
  @Input() auditUsers: LookupModel[] = [];

  hasSupportAccess = false;
  additionalFilter: any = {};
  recIdFilter: string;
  searchText: string;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource: AuditDatasource;
  detailsUrl: string;
  hasEditAccess: boolean;



  constructor(public dialog: MatDialog,
              public headerService: HeaderService, public authService: AuthService,
              private auditApiService: AuditApiService,
              private auditService: AuditService,
              private router: Router,
              private route: ActivatedRoute) {
  }

  ngOnInit() {
    this.hasSupportAccess = this.authService.hasAccess([RolesEnum.Support]);
    this.hasEditAccess = this.authService.hasAccess([RolesEnum.AccountUsers_Edit, RolesEnum.AccountUsers_Delete]);
    this.dataSource = new AuditDatasource(this.auditApiService);
    this.detailsUrl = '../user';
  }

  ngAfterViewInit() {
    this.filterChangeEvent?.subscribe((filter) => {
      this.additionalFilter = filter;
      this.paginator.pageIndex = 0;
    });

    this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);

    merge(this.sort.sortChange, this.paginator.page, this.filterChangeEvent)
      .pipe(debounceTime(10))
      .pipe(
        tap(() => this.loadData())
      )
      .subscribe();
  }

  loadData() {
    console.log('filter changed');
    this.dataSource.queryData(this.getDataFilter());
  }

  getDataFilter(): AuditListRequest {
    const baseRequest = {
      skip: this.paginator.pageIndex * this.paginator.pageSize,
      limit: this.paginator.pageSize,
      sort: [{field: this.sort.active, direction: this.sort.direction ? this.sort.direction.toUpperCase() : undefined}],
    };

    return Object.assign(baseRequest, this.additionalFilter || {});
  }

  ngOnDestroy() {
  }

  getTypeName(type: number): string {
    const lookup = this.auditTypes.find(x => x.intId === type);
    if (lookup) {
      return lookup.text;
    }
    return 'Unknown';
  }

  getCategoryName(category: number): string {
    const lookup = this.auditCategories.find(x => x.intId === category);
    if (lookup) {
      return lookup.text;
    }
    return 'Unknown';
  }

  navigateToDetails(dataItem: AuditModel): void {
    const newRecord: any = JSON.parse(dataItem.newRecordJson || '');
    if (dataItem.type === AuditTypeEnum.LOGIN || dataItem.type === AuditTypeEnum.LOGOUT) {
      const hasAccess = this.authService.hasAccess([
        RolesEnum.AccountUsers_View,
        RolesEnum.AccountUsers_Edit,
        RolesEnum.AccountUsers_Delete]);

      if (hasAccess) {
        const module = RouterHelper.getRouteByComponent('UserComponent');
        const path = this.headerService.themePrefix + module?.path;
        this.router.navigate([path, dataItem.customerUserId]);
        return;
      }
    }

    const hasCallLogsAccess = this.authService.hasAccess([
      RolesEnum.CallLogs_View,
      RolesEnum.CallLogs_Edit,
      RolesEnum.CallLogs_Delete]);

    if (dataItem.category === AuditCategoryEnum.RECORDING) {
      if (hasCallLogsAccess) {
        const module = RouterHelper.getRouteByComponent('CallLogsComponent');
        const path = this.headerService.themePrefix + module?.path;
        const navigationExtras: NavigationExtras = {
          queryParams: {'callRecId': newRecord.callRecId}
        };
        this.router.navigate([path], navigationExtras);
        return;
      }
    }

    if (dataItem.category === AuditCategoryEnum.RECORDING_V2) {
      if (hasCallLogsAccess) {
        const module = RouterHelper.getRouteByComponent('CallLogsV2Component');
        const path = this.headerService.themePrefix + module?.path;
        const navigationExtras: NavigationExtras = {
          queryParams: {'callRecId': newRecord.callRecId}
        };
        this.router.navigate([path], navigationExtras);
        return;
      }
    }

    if (dataItem.category === AuditCategoryEnum.CALL_LOGS) {
      if (hasCallLogsAccess) {
        const module = RouterHelper.getRouteByComponent('CallLogsComponent');
        const path = this.headerService.themePrefix + module?.path;
        const navigationExtras: NavigationExtras = {
          // queryParams: { 'callRecId': dataItem.newRecord.callRecId }
        };
        this.router.navigate([path], navigationExtras);
        return;
      }
    }

    if (dataItem.category === AuditCategoryEnum.CALL_LOGS_V2) {
      if (hasCallLogsAccess) {
        const module = RouterHelper.getRouteByComponent('CallLogsV2Component');
        const path = this.headerService.themePrefix + module?.path;
        const navigationExtras: NavigationExtras = {
          // queryParams: { 'callRecId': dataItem.newRecord.callRecId }
        };
        this.router.navigate([path], navigationExtras);
        return;
      }
    }

    if (dataItem.category === AuditCategoryEnum.ADMIN && dataItem.tableName === 'cp_role') {
      if (dataItem.type === AuditTypeEnum.DELETE) {
        const hasAccess = this.authService.hasAccess([
          RolesEnum.AccountRoles_View,
          RolesEnum.AccountRoles_Edit,
          RolesEnum.AccountRoles_Delete]);

        if (hasAccess) {
          const module = RouterHelper.getRouteByComponent('RolesComponent');
          const path = this.headerService.themePrefix + module?.path;
          this.router.navigate([path]);
          return;
        }
      } else {
        const hasAccess = this.authService.hasAccess([
          RolesEnum.AccountRoles_View,
          RolesEnum.AccountRoles_Edit,
          RolesEnum.AccountRoles_Delete]);

        if (hasAccess) {
          const module = RouterHelper.getRouteByComponent('RoleComponent');
          const path = this.headerService.themePrefix + module?.path;
          this.router.navigate([path, newRecord.id]);
          return;
        }
      }
    }

    if (dataItem.category === AuditCategoryEnum.ADMIN && dataItem.tableName === 'cp_user') {
      if (dataItem.type === AuditTypeEnum.DELETE) {
        const hasAccess = this.authService.hasAccess([
          RolesEnum.AccountUsers_View,
          RolesEnum.AccountUsers_Edit,
          RolesEnum.AccountUsers_Delete]);

        if (hasAccess) {
          const module = RouterHelper.getRouteByComponent('UsersComponent');
          const path = this.headerService.themePrefix + module?.path;
          this.router.navigate([path]);
          return;
        }
      } else {
        const hasAccess = this.authService.hasAccess([
          RolesEnum.AccountUsers_View,
          RolesEnum.AccountUsers_Edit,
          RolesEnum.AccountUsers_Delete]);

        if (hasAccess) {
          const module = RouterHelper.getRouteByComponent('UserComponent');
          const path = this.headerService.themePrefix + module?.path;
          this.router.navigate([path, newRecord.id]);
          return;
        }
      }
    }

    if (dataItem.category === AuditCategoryEnum.ADMIN && dataItem.tableName === 'cp_storage') {
      const hasAccess = this.authService.hasAccess([
        RolesEnum.AccountStorage_View,
        RolesEnum.AccountStorage_Edit,
        RolesEnum.AccountStorage_Delete]);

      if (hasAccess) {
        const module = RouterHelper.getRouteByComponent('StorageComponent');
        const path = this.headerService.themePrefix + module?.path;
        this.router.navigate([path]);
        return;
      }
    }

    if (dataItem.category === AuditCategoryEnum.ADMIN && dataItem.tableName === 'cp_settings') {
      const hasAccess = this.authService.hasAccess([
        RolesEnum.AccountSettings_View,
        RolesEnum.AccountSettings_Edit,
        RolesEnum.AccountSettings_Delete]);

      if (hasAccess) {
        const module = RouterHelper.getRouteByComponent('SettingsComponent');
        const path = this.headerService.themePrefix + module?.path;
        this.router.navigate([path]);
        return;
      }
    }

    if (dataItem.category === AuditCategoryEnum.FIREWALL && dataItem.tableName === 'ipaddress') {
      if (dataItem.type === AuditTypeEnum.DELETE) {
        const hasAccess = this.authService.hasAccess([
          RolesEnum.Firewall_View,
          RolesEnum.Firewall_Edit,
          RolesEnum.Firewall_Delete]);

        if (hasAccess) {
          const module = RouterHelper.getRouteByComponent('WhiteListComponent');
          const path = this.headerService.themePrefix + module?.path;
          this.router.navigate([path]);
          return;
        }
      } else {
        const hasAccess = this.authService.hasAccess([
          RolesEnum.Firewall_View,
          RolesEnum.Firewall_Edit,
          RolesEnum.Firewall_Delete]);

        if (hasAccess) {
          const module = RouterHelper.getRouteByComponent('FirewallEditComponent');
          const path = this.headerService.themePrefix + module?.path.replace('edit', '');
          this.router.navigate([path, newRecord.id, 'edit']);
          return;
        }
      }
    }

    return;
  }
}
